import { useLocation } from "wouter";
import { useEffect } from "react";
import { ReportsLayout } from "@/components/layouts";
import { Metric } from "@/features/reports/components/metric";
import { useGetAllAssetInsights } from "@/api/studio/insights/queries";
import { Card, CardBody, CardSkeleton } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Table, TableHeader, TableRow, TableCell } from "@/components/ui/table";
import { ReportBarChart } from "@/features/reports/components/report-bar-chart";
import { DigestibilityIcon, VisibilityIcon, ClarityIcon } from "@/assets/icons";
import { ReportNavToolbar } from "@/features/reports/components/report-nav-toolbar";
import { useReportsContext } from "@/features/reports/contexts/reports-context";
import { useUpdateRoute } from "@/features/studies/hooks/use-update-route";
import { useSegment } from "@/hooks/use-segment";
import { useGlobalStore } from "@/stores/global-store";
import { getTopScore } from "@/features/reports/utils/get-top-score";
import { getThumbnailImage } from "@/features/reports/utils/get-thumbnail-image";
import { useReportFiltersStore } from "@/features/reports/stores/use-report-filters-store";

/* Prop Types */
interface Props {
  projectId: string;
  studyId: string;
}

/*
 * ReportsAttentionOverviewRoute component
 * Route for Attention Overview
 */
export const ReportsAttentionOverviewRoute = ({ projectId, studyId }: Props) => {
  // Update the route with the current step
  useUpdateRoute(projectId, studyId, "reports/attention/overview");

  /* Stores */
  const [, navigate] = useLocation();
  const theme = useGlobalStore((state) => state.theme);
  const sortBy = useReportFiltersStore((state) => state.sortBy);
  const orderBy = useReportFiltersStore((state) => state.orderBy);

  /* Context */
  const {
    state: { view },
    actions: { setReportType, setAssetKey },
  } = useReportsContext();

  /* Hooks */
  const { segmentPageEvent, segmentTrackEvent } = useSegment();

  /* Queries */
  const { data: allInsights, isLoading: isAllInsightsLoading } = useGetAllAssetInsights({ projectId, studyId, orderBy, sortBy });
  /* Consts */
  const hasContext = !!allInsights?.[0]?.metrics?.attention?.visibility;

  /* Handlers */
  const handleClick = (assetId: string) => {
    // segment track event when a user clicks on a report asset
    segmentTrackEvent("Asset Report Viewed", {
      studyId,
      projectId,
      assetId,
      reportType: "attention",
      studyType: "asset",
    });
    // set the report type and assetKey
    setReportType("attention");
    setAssetKey(assetId);
    // navigate to the in-depth report page for the selected asset
    navigate(`/projects/${projectId}/studies/${studyId}/asset/reports/in-depth`);
  };

  /* Side effects */
  useEffect(() => {
    // segment page event when a user visits the reports overview page
    if (theme)
      segmentPageEvent("Study Report", {
        title: "Attention Overview Report | Dragonfly AI Studio",
        theme: theme,
      });
  }, []);

  /* Render */
  return (
    <ReportsLayout projectId={projectId} studyId={studyId}>
      {/* Navbar */}
      <ReportNavToolbar projectId={projectId} studyId={studyId} currentTab="attention" hasContext={hasContext} />

      {/* Body */}
      <div className="grow flex flex-row gap-4 overflow-hidden">
        <div className="grow flex flex-col gap-4 overflow-y-auto">
          {/* Cards */}
          {view === "Cards" && (
            <div className="flex flex-row gap-4 flex-wrap">
              {/* Loading */}
              {isAllInsightsLoading && Array.from({ length: 4 }).map((_, index) => <CardSkeleton key={index} />)}

              {/* Insights */}
              {allInsights &&
                allInsights.map((insight, index) => (
                  <Card
                    key={insight.key}
                    onClick={() => handleClick(insight.key)}
                    imageUrl={getThumbnailImage(insight.key, 300, 300, projectId, studyId)}
                    imgFit="contain"
                    winner={getTopScore(index, allInsights.length, sortBy)}
                  >
                    <CardBody className="flex flex-col gap-5 px-4 py-5">
                      <div className="flex flex-col gap-2 truncate">
                        <p className="text-xs truncate">{insight.assetName}</p>
                        <Metric title="Attention" className="text-base font-semibold" value={insight.metrics.attention.score} />
                      </div>

                      <div className="flex flex-col gap-3">
                        {hasContext && <Metric title="Visibility" size="small" value={insight.metrics.attention.visibility.score} icon={<VisibilityIcon className="size-4" />} />}
                        <Metric title="Clarity" size="small" value={insight.metrics.attention.clarity.score} icon={<ClarityIcon className="size-4" />} />
                        <Metric title="Digestibility" size="small" value={insight.metrics.attention.digestibility.score} icon={<DigestibilityIcon className="size-4" />} />
                      </div>
                    </CardBody>
                  </Card>
                ))}
            </div>
          )}

          {/* Chart */}
          {view === "Chart" && allInsights && (
            <>
              <div className="max-h-80 min-h-80 h-80 grow border-b border-base-300">
                <ReportBarChart insightData={allInsights} projectId={projectId} studyId={studyId} />
              </div>
              <div className="flex-1 flex flex-col gap-4">
                <Table>
                  <thead>
                    <TableRow>
                      <TableHeader className="w-72">Assets</TableHeader>
                      <TableHeader className="w-40">Ranking</TableHeader>
                      <TableHeader className="w-56">
                        <div className="flex flex-row items-center gap-3">
                          <span className="flex w-4 h-4 rounded-sm bg-primary"></span>
                          Attention
                        </div>
                      </TableHeader>
                      {hasContext && (
                        <TableHeader className="w-56">
                          <div className="flex flex-row items-center gap-3">
                            <span className="flex w-4 h-4 rounded-sm bg-accent"></span>
                            Visibility
                          </div>
                        </TableHeader>
                      )}
                      <TableHeader className="w-56">
                        <div className="flex flex-row items-center gap-3">
                          <span className="flex w-4 h-4 rounded-sm bg-secondary"></span>
                          Clarity
                        </div>
                      </TableHeader>
                      <TableHeader className="w-56">
                        <div className="flex flex-row items-center gap-3">
                          <span className="flex w-4 h-4 rounded-sm bg-warning"></span>
                          Digestibility
                        </div>
                      </TableHeader>
                    </TableRow>
                  </thead>
                  <tbody>
                    {allInsights?.map((insight, index) => (
                      <TableRow key={insight.key}>
                        <TableCell className="truncate">
                          <img src={getThumbnailImage(insight.key, 150, 150, projectId, studyId)} className="rounded-lg w-12 h-12 object-cover shrink-0" />
                          <small className="truncate">{insight.assetName}</small>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-row items-center gap-2">
                            {sortBy === "asc" ? allInsights.length - index : index + 1}
                            {getTopScore(index, allInsights.length, sortBy) && <div className="bg-pink-500 text-white px-2 py-1 rounded-lg text-xs font-medium">Top score</div>}
                          </div>
                        </TableCell>
                        <TableCell>{insight.metrics.attention.score}%</TableCell>
                        {hasContext && <TableCell>{insight.metrics.attention.visibility.score}%</TableCell>}
                        <TableCell>{insight.metrics.attention.clarity.score}%</TableCell>
                        <TableCell>{insight.metrics.attention.digestibility.score}%</TableCell>
                      </TableRow>
                    ))}
                  </tbody>
                </Table>
              </div>
            </>
          )}

          {/* Table */}
          {view === "List" && allInsights && (
            <div className="flex-1 flex flex-col gap-4">
              <Table>
                <thead>
                  <TableRow>
                    <TableHeader>Assets</TableHeader>
                    <TableHeader className="w-40">Ranking</TableHeader>
                    <TableHeader className="w-56">Attention</TableHeader>
                    {hasContext && <TableHeader className="w-56">Visibility</TableHeader>}
                    <TableHeader className="w-56">Clarity</TableHeader>
                    <TableHeader className="w-56">Digestibility</TableHeader>
                  </TableRow>
                </thead>
                <tbody>
                  {allInsights.map((insight, index) => (
                    <TableRow key={insight.key} onClick={() => handleClick(insight.key)}>
                      <TableCell className="truncate">
                        <img src={getThumbnailImage(insight.key, 150, 150, projectId, studyId)} className="rounded-lg w-12 h-12 object-cover shrink-0" />
                        <small className="truncate">{insight.assetName}</small>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-row items-center gap-2">
                          {sortBy === "asc" ? allInsights.length - index : index + 1}
                          {getTopScore(index, allInsights.length, sortBy) && <div className="bg-pink-500 text-white px-2 py-1 rounded-lg text-xs font-medium">Top score</div>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Progress value={insight.metrics.attention.score} className="w-20" />
                        {insight.metrics.attention.score}%
                      </TableCell>
                      {hasContext && (
                        <TableCell>
                          <Progress value={insight.metrics.attention.visibility.score} className="w-20" />
                          {insight.metrics.attention.visibility.score}%
                        </TableCell>
                      )}
                      <TableCell>
                        <Progress value={insight.metrics.attention.clarity.score} className="w-20" />
                        {insight.metrics.attention.clarity.score}%
                      </TableCell>
                      <TableCell>
                        <Progress value={insight.metrics.attention.digestibility.score} className="w-20" />
                        {insight.metrics.attention.digestibility.score}%
                      </TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </div>
      </div>
    </ReportsLayout>
  );
};
